/**
 * Phira's 33 tween functions (`TWEEN_FUNCTIONS`, F§6.5, P§1.4, R§2.1) in f32 emulation.
 *
 * Reimplemented from the formulas in docs/research (no Phira code copied):
 *  - every arithmetic step is rounded with `Math.fround` (an f64 operation on f32 operands followed by `fround`
 *    is the correctly rounded f32 operation for + - * / and sqrt);
 *  - transcendental functions (cos, sin, pow) are evaluated in f64 and rounded to f32, which differs from a libm
 *    `cosf`/`powf` by at most about one f32 ulp;
 *  - Out and InOut are always built from the In base (`OUT(f)(x) = 1 - f(1 - x)`,
 *    `INOUT(f)(x) = 2x < 1 ? f(2x) / 2 : 1 - f(2 - 2x) / 2`), so InOutBack and InOutElastic are Phira's curves,
 *    not RPE's (F§6.5);
 *  - no endpoint special cases: InExpo(0) = 2^-10, InElastic(0) is about -0.000488;
 *  - x is never clamped: values below 0 or above 1 extrapolate (Circ gives NaN where 1 - x^2 < 0).
 *
 * Tween ids: `major * 3 + minor` with major Plain 0, Sine 1, Quad 2, Cubic 3, Quart 4, Quint 5, Expo 6, Circ 7,
 * Back 8, Elastic 9, Bounce 10 and minor In 0, Out 1, InOut 2. Plain ids: 0 = constant 0 (hold), 1 = constant 1,
 * 2 = linear.
 */

import type { EaseFamily, EaseVariant, TweenId } from './types';

const fr = Math.fround;

/** `std::f32::consts::PI`. */
export const PI_F32 = fr(Math.PI);
/** Back: C1 = 1.70158, C3 = C1 + 1 (f32). */
export const BACK_C1 = fr(1.70158);
export const BACK_C3 = fr(BACK_C1 + 1);
/** Elastic: C4 = (2 * PI) / 3 (f32). */
export const ELASTIC_C4 = fr(fr(2 * PI_F32) / 3);
/** Bounce constants (f32 constant expressions, each one correctly rounded). */
export const BOUNCE_N1 = 7.5625;
export const BOUNCE_D1 = 2.75;
const BOUNCE_T1 = fr(1 / BOUNCE_D1);
const BOUNCE_T2 = fr(2 / BOUNCE_D1);
const BOUNCE_T3 = fr(2.5 / BOUNCE_D1);
const BOUNCE_O2 = fr(1.5 / BOUNCE_D1);
const BOUNCE_O3 = fr(2.25 / BOUNCE_D1);
const BOUNCE_O4 = fr(2.625 / BOUNCE_D1);

/** Number of Phira tween ids (0..32). */
export const TWEEN_COUNT = 33;
/** Plain tween ids. */
export const HOLD_TWEEN: TweenId = 0;
export const ONE_TWEEN: TweenId = 1;
export const LINEAR_TWEEN: TweenId = 2;

export type TweenFn = (x: number) => number;

// ---------------------------------------------------------------------------------------------------------------
// "In" base functions (x is an f32 value; the result is an f32 value)
// ---------------------------------------------------------------------------------------------------------------

/** `1 - cos((x * PI) / 2)`. */
export function sineIn(x: number): number {
  return fr(1 - fr(Math.cos(fr(fr(x * PI_F32) / 2))));
}

/** `x * x`. */
export function quadIn(x: number): number {
  return fr(x * x);
}

/** `x * x * x`. */
export function cubicIn(x: number): number {
  return fr(fr(x * x) * x);
}

/** `x * x * x * x`. */
export function quartIn(x: number): number {
  return fr(fr(fr(x * x) * x) * x);
}

/** `x * x * x * x * x`. */
export function quintIn(x: number): number {
  return fr(fr(fr(fr(x * x) * x) * x) * x);
}

/** `2^(10 * (x - 1))` (so expo(0) = 2^-10, not 0). */
export function expoIn(x: number): number {
  return fr(Math.pow(2, fr(10 * fr(x - 1))));
}

/** `1 - sqrt(1 - x * x)` (NaN for |x| > 1). */
export function circIn(x: number): number {
  return fr(1 - fr(Math.sqrt(fr(1 - fr(x * x)))));
}

/** `(C3 * x - C1) * x * x`. */
export function backIn(x: number): number {
  return fr(fr(fr(fr(BACK_C3 * x) - BACK_C1) * x) * x);
}

/** `-(2^(10x - 10) * sin((x * 10 - 10.75) * C4))` (elastic(0) is about -0.000488). */
export function elasticIn(x: number): number {
  const p = fr(Math.pow(2, fr(fr(10 * x) - 10)));
  const s = fr(Math.sin(fr(fr(fr(x * 10) - 10.75) * ELASTIC_C4)));
  return -fr(p * s);
}

/**
 * Bounce In = `1 - B(1 - x)` with the four-parabola out-bounce `B`:
 * `N1 u^2` (u < 1/D1), `N1 (u - 1.5/D1)^2 + 0.75` (u < 2/D1), `N1 (u - 2.25/D1)^2 + 0.9375` (u < 2.5/D1),
 * else `N1 (u - 2.625/D1)^2 + 0.984375`.
 */
export function bounceIn(x: number): number {
  return fr(1 - bounceOutBase(fr(1 - x)));
}

/** The out-bounce base `B(u)` (exported for the integrals). */
export function bounceOutBase(u: number): number {
  if (u < BOUNCE_T1) return fr(fr(BOUNCE_N1 * u) * u);
  if (u < BOUNCE_T2) {
    const d = fr(u - BOUNCE_O2);
    return fr(fr(BOUNCE_N1 * fr(d * d)) + 0.75);
  }
  if (u < BOUNCE_T3) {
    const d = fr(u - BOUNCE_O3);
    return fr(fr(BOUNCE_N1 * fr(d * d)) + 0.9375);
  }
  const d = fr(u - BOUNCE_O4);
  return fr(fr(BOUNCE_N1 * fr(d * d)) + 0.984375);
}

/** Bounce piece boundaries and offsets (f32), exported for the integrals. */
export const BOUNCE_PIECES = {
  t1: BOUNCE_T1,
  t2: BOUNCE_T2,
  t3: BOUNCE_T3,
  o2: BOUNCE_O2,
  o3: BOUNCE_O3,
  o4: BOUNCE_O4,
} as const;

// ---------------------------------------------------------------------------------------------------------------
// Variant builders
// ---------------------------------------------------------------------------------------------------------------

export function outOf(f: TweenFn): TweenFn {
  return (x) => fr(1 - f(fr(1 - x)));
}

export function inOutOf(f: TweenFn): TweenFn {
  return (x) => {
    const x2 = fr(x * 2);
    return x2 < 1 ? fr(f(x2) / 2) : fr(1 - fr(f(fr(2 - x2)) / 2));
  };
}

/** Families in major order (major = index + 1). */
export const TWEEN_FAMILIES: readonly Exclude<EaseFamily, 'linear'>[] = [
  'sine',
  'quad',
  'cubic',
  'quart',
  'quint',
  'expo',
  'circ',
  'back',
  'elastic',
  'bounce',
];

/** The In base of each family. */
export const IN_BASES: Readonly<Record<Exclude<EaseFamily, 'linear'>, TweenFn>> = {
  sine: sineIn,
  quad: quadIn,
  cubic: cubicIn,
  quart: quartIn,
  quint: quintIn,
  expo: expoIn,
  circ: circIn,
  back: backIn,
  elastic: elasticIn,
  bounce: bounceIn,
};

const VARIANTS: readonly EaseVariant[] = ['in', 'out', 'inout'];

function buildTable(): readonly TweenFn[] {
  const table: TweenFn[] = [() => 0, () => 1, (x) => x];
  for (const family of TWEEN_FAMILIES) {
    const f = IN_BASES[family];
    table.push(f, outOf(f), inOutOf(f));
  }
  return table;
}

/** `TWEEN_FUNCTIONS[0..32]`; each function expects an f32 argument. */
export const TWEEN_TABLE: readonly TweenFn[] = buildTable();

/** `easing_from(major, minor) = major * 3 + minor`. */
export function tweenIdOf(family: EaseFamily, variant: EaseVariant | null): TweenId {
  if (family === 'linear') return LINEAR_TWEEN;
  const major = TWEEN_FAMILIES.indexOf(family) + 1;
  const minor = VARIANTS.indexOf(variant ?? 'in');
  return major * 3 + minor;
}

/** Family and variant of a tween id (plain ids 0..2 report family null). */
export function tweenShape(id: TweenId): {
  readonly family: Exclude<EaseFamily, 'linear'> | null;
  readonly variant: EaseVariant | null;
} {
  assertTweenId(id);
  if (id < 3) return { family: null, variant: null };
  const family = TWEEN_FAMILIES[Math.floor(id / 3) - 1] ?? null;
  const variant = VARIANTS[id % 3] ?? null;
  return { family, variant };
}

export function isTweenId(id: number): boolean {
  return Number.isInteger(id) && id >= 0 && id < TWEEN_COUNT;
}

export function assertTweenId(id: number): void {
  if (!isTweenId(id)) throw new RangeError(`core/ease: invalid Phira tween id ${String(id)} (expected 0..32)`);
}

/**
 * `TWEEN_FUNCTIONS[id](x)` in f32: x is rounded to f32 first (Phira passes `progress as f32`), the result is an
 * f32 value. x is not clamped.
 */
export function staticY(id: TweenId, x: number): number {
  const f = TWEEN_TABLE[id];
  if (f === undefined || !Number.isInteger(id)) {
    throw new RangeError(`core/ease: invalid Phira tween id ${String(id)} (expected 0..32)`);
  }
  return f(fr(x));
}
